// Axios-specific cancellation type, thrown when a request's signal is aborted. An axios major
// upgrade that renames or reshapes it has to be reflected in the catches below, or every
// superseded request starts dispatching an error - see tests/services/hardwareInventory.
import { CanceledError } from 'axios';
import { EDITION_JOB_POLL, GET_EDITIONS_DEFAULT_QUERY_PARAMS } from '../../constants/editions';
import { getApiErrorMessage } from '../../helpers/apiError';
import * as editionsService from '../../services/editions';
import { describeJobOutcome, isJobSucceeded, pollJobUntilTerminal } from '../../services/jobs';
import { pushDangerNotification, pushSuccessfulNotification, pushWarningNotification } from '../notifications/actions';
import * as actionTypes from './actionTypes';

const UNCONFIRMED_MESSAGE = 'The edition was submitted but is taking longer than expected to apply. Refresh the list in a moment to confirm.';

/*
 * `services/jobs` knows what a `skipped` or `suppressed` job means, not what the job was doing, so
 * the noun is supplied here.
 */
const UNAPPLIED_MESSAGE = 'The edition was not updated';

export const showEditionViewModal = (edition) => {
	return { type: actionTypes.SHOW_EDITION_VIEW_MODAL, payload: edition };
};

export const hideEditionViewModal = () => {
	return { type: actionTypes.HIDE_EDITION_VIEW_MODAL };
};

export const showLicenseEditModal = (licenseId) => {
	return { type: actionTypes.SHOW_LICENSE_EDIT_MODAL, payload: licenseId };
};

export const hideLicenseEditModal = () => {
	return { type: actionTypes.HIDE_LICENSE_EDIT_MODAL };
};

export const showEditionEditModal = (edition) => {
	return { type: actionTypes.SHOW_EDITION_EDIT_MODAL, payload: edition };
};

export const hideEditionEditModal = () => {
	return { type: actionTypes.HIDE_EDITION_EDIT_MODAL };
};

export const getEditions = (queryParams = GET_EDITIONS_DEFAULT_QUERY_PARAMS) => {
	return (dispatch) => {
		dispatch({ type: actionTypes.START_FETCHING_EDITIONS });

		return editionsService
			.getEditions(queryParams)
			.then(({ data }) => {
				dispatch({
					type: actionTypes.GET_EDITIONS_SUCCESS,
					payload: {
						editions: data?.items ?? [],
						pagination: data?.pagination ?? {}
					}
				});

				return data?.items ?? [];
			})
			.catch((error) => {
				/*
				 * The service aborts the previous read on every new one, so a cancellation is the
				 * expected outcome of paging quickly rather than a failure.
				 */
				if (error instanceof CanceledError) {
					return;
				}

				dispatch({
					type: actionTypes.GET_EDITIONS_ERROR,
					payload: getApiErrorMessage(error, 'The editions could not be loaded.')
				});
			});
	};
};

const isAbort = (error) => {
	return error instanceof CanceledError || error?.name === 'AbortError';
};

const abandonEditionWrite = (dispatch) => {
	return dispatch({ type: actionTypes.SAVE_EDITION_ABORTED });
};

const failEditionWrite = (dispatch, message) => {
	dispatch({ type: actionTypes.SAVE_EDITION_ERROR, payload: message });
	dispatch(pushDangerNotification(message));
};

/*
 * The write pipeline an edition change runs through: send, then poll the job it returns, then
 * refresh the list. Three failure shapes, handled differently:
 *
 *   Request rejected      - nothing was accepted (a 422, a 409, an expired session). The modal
 *                           stays open with the tiers as picked so they can be corrected in place.
 *   Poll never concluded  - the write WAS accepted; all that failed is watching it finish. Saying
 *                           it failed would be wrong, so this closes the modal, refreshes the list
 *                           and says the outcome is unconfirmed.
 *   Job ended un-succeeded - pollJobUntilTerminal resolves on `failed`, `canceled`, `skipped` and
 *                           `suppressed` too, so the status is classified before anything is
 *                           called a success.
 *
 * Resolves rather than rejects on a handled failure, so the caller never has to catch.
 */
const runEditionWrite = async (dispatch, sendRequest, options) => {
	const { successMessage, failureMessage, queryParams, signal } = options;
	let job;

	dispatch({ type: actionTypes.START_SAVING_EDITION });

	let jobId;

	try {
		const { data } = await sendRequest();

		jobId = data?.jobId;
	} catch (error) {
		if (isAbort(error)) {
			return abandonEditionWrite(dispatch);
		}

		return failEditionWrite(dispatch, getApiErrorMessage(error, failureMessage));
	}

	if (!jobId) {
		return failEditionWrite(dispatch, 'The request was accepted but returned no job to track.');
	}

	try {
		job = await pollJobUntilTerminal(jobId, { ...EDITION_JOB_POLL, signal });
	} catch (error) {
		if (isAbort(error)) {
			return abandonEditionWrite(dispatch);
		}

		dispatch({ type: actionTypes.SAVE_EDITION_ERROR, payload: UNCONFIRMED_MESSAGE });
		dispatch(pushWarningNotification(UNCONFIRMED_MESSAGE));
		dispatch(hideEditionEditModal());

		return dispatch(getEditions(queryParams));
	}

	if (!isJobSucceeded(job)) {
		return failEditionWrite(dispatch, describeJobOutcome(job, {
			failureMessage: failureMessage,
			unappliedMessage: UNAPPLIED_MESSAGE
		}));
	}

	dispatch({ type: actionTypes.SAVE_EDITION_SUCCESS });
	dispatch(hideEditionEditModal());
	dispatch(pushSuccessfulNotification(successMessage));

	/*
	 * The refresh is awaited inside the same promise rather than fired and forgotten, so the Save
	 * button stays disabled until the table actually shows the change.
	 */
	return dispatch(getEditions(queryParams));
};

/*
 * `licenses` is the edition's whole list: the PATCH replaces it, so a changed tier travels with
 * the unchanged rows around it. Only `licenses` is sent - the name, description and code are not
 * edited here, and the id never goes in a body.
 */
export const updateEditionLicenses = (id, licenses, { queryParams, signal } = {}) => {
	return (dispatch) => {
		return runEditionWrite(dispatch, () => {
			return editionsService.updateEdition(id, { licenses: licenses });
		}, {
			successMessage: 'Edition updated.',
			failureMessage: 'The edition could not be updated.',
			queryParams: queryParams,
			signal: signal
		});
	};
};
